// api.js
import axios from 'axios';
import { getDeviceType, getDeviceUUID } from './utils/deviceManager';

const API = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
});

// Vérifier si un token est expiré
const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return decoded.exp * 1000 < Date.now();
  } catch (e) {
    return true;
  }
};

API.interceptors.request.use((config) => {
  // Only add device headers for non-registration requests
  const isRegisterEndpoint = config.url?.includes('/api/auth/register');
  
  if (!isRegisterEndpoint) {
    // Add device information to all requests except registration
    config.headers['X-Device-Type'] = getDeviceType();
    config.headers['X-Device-Id'] = getDeviceUUID();
  }
  
  // Prioritize adminToken for admin endpoints, otherwise use regular token
  const isAdminRequest = config.url?.includes('/api/admin/');
  let token = isAdminRequest 
    ? localStorage.getItem('adminToken') 
    : localStorage.getItem('token') || localStorage.getItem('adminToken');
  
  // Vérifier si le token est expiré avant de l'utiliser
  if (token && isTokenExpired(token)) {
    // Si token expiré, le supprimer
    if (isAdminRequest) {
      localStorage.removeItem('adminToken');
    } else {
      localStorage.removeItem('token');
    }
    token = null; // Ne pas utiliser un token expiré
  }
  
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Ajout de l'intercepteur de réponse pour gérer les erreurs d'authentification
API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Ne pas rediriger si on est déjà sur la page de login ou dans un processus de login
      const isLoginPage = window.location.pathname === '/' || 
                         window.location.pathname === '/login' || 
                         window.location.pathname === '/admin/login';
      const isLoginRequest = error.config.url?.includes('/api/auth/login');
      
      if (!isLoginPage && !isLoginRequest) {
        // Stocker le message d'erreur pour l'afficher sur la page de login
        localStorage.setItem('authError', 'Votre session a expiré. Veuillez vous reconnecter.');
        
        // Supprimer les tokens d'authentification
        localStorage.removeItem('token');
        localStorage.removeItem('adminToken');
        
        // Rediriger vers la page de login
        const isAdminRoute = window.location.pathname.includes('/admin/');
        window.location.href = isAdminRoute ? '/admin/login' : '/';
      }
    }
    return Promise.reject(error);
  }
);

// Fonction utilitaire pour décoder un JWT (sans vérification de signature)
export function decodeJWT(token) {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

// Fonction pour rafraîchir le token utilisateur
export async function refreshUserToken() {
  const token = localStorage.getItem('token');
  if (!token) return null;
  try {
    const response = await API.post('/api/auth/refresh-token', null, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (response.data?.access_token) {
      localStorage.setItem('token', response.data.access_token);
      return response.data.access_token;
    }
    return null;
  } catch {
    // En cas d'échec, on ne fait rien (l'utilisateur sera déconnecté à l'expiration)
    return null;
  }
}

export default API;
